import { ReactElement, useEffect, useState } from "react";
import useIsAuthorized from "../../Common/UI/hook/useIsAuthorized";
import { appRoles, config } from "../../authConfig";
import { FirstRow } from "./FirstRow";
import { SecondRow } from "./SecondRow";
import dayjs from "dayjs";
import useFetchMsal from "../../Common/UI/hook/useFetchMsal";
import { AutomatedTestResult, CardFreeInfo, DoorDash, DoorDashFeatureFlag, POSStatus, UberEats, WANStatuses } from "./Interfaces";
import useUserRoles from "../../Common/UI/hook/useUserRoles";
import { ThirdRow } from "./ThirdRow";

export const Page = function (): ReactElement {
  const execute = useFetchMsal();

  const [loadingWANStatuses, setLoadingWANStatuses] = useState<boolean>(true);
  const [WANStatuses, setWANStatuses] = useState<WANStatuses[]>([]);
  const [loadingCardFreeStatuses, setLoadingCardFreeStatuses] = useState<boolean>(true);
  const [storeCardFreeStatuses, setStoreCardFreeStatuses] = useState<CardFreeInfo[]>([]);
  const [loadingPOSStatuses, setLoadingPOSStatuses] = useState<boolean>(true);
  const [storePOSStatuses, setStorePOSStatuses] = useState<POSStatus[]>([]);
  const [loadingUberStatuses, setLoadingUberStatuses] = useState<boolean>(true);
  const [loadingDoorDashStatuses, setLoadingDoorDashStatuses] = useState<boolean>(true);
  const [loadingDoorDashFeatureFlag, setLoadingDoorDashFeatureFlag] = useState<boolean>(true);
  const [uberStatuses, setUberStatuses] = useState<UberEats[]>([]);
  const [doorDashStatuses, setDoorDashStatuses] = useState<DoorDash[]>([]);
  const [doorDashFeatureFlag, setDoorDashFeatureFlag] = useState<DoorDashFeatureFlag>({ enabled: false });
  const [loadingWebsiteTests, setLoadingWebsiteTests] = useState<boolean>(false);

  const { roles, isLoading } = useUserRoles();
  const showCardFreeInfo = useIsAuthorized([appRoles.Home.CardFreeStatus], roles);
  const showWANInfo = useIsAuthorized([appRoles.Home.FirewallStatus], roles);
  const showPOSStatus = useIsAuthorized([appRoles.Home.ViewPOSStatus], roles);
  const showUberStatus = useIsAuthorized([appRoles.Home.ViewUberEatsStatus], roles);
  const showDoorDash = useIsAuthorized([appRoles.Home.ViewDoorDashStatus], roles);
  const showDoorDashFeatureFlag = useIsAuthorized([appRoles.Home.ViewDoorDashStatus], roles);

  const showWebsiteStatus = useIsAuthorized([appRoles.DevDashboard], roles);
  const [automatedTestResult, setAutomatedTestResult] = useState<AutomatedTestResult[]>([]);

  const loadWAN = function (date: string) {
    if (showWANInfo) {
      const url = config.rootAPIList.storeWANStatus;
      setLoadingWANStatuses(true);
      execute("GET", `${config.rootAPIUrl}${url}`)
        .then((result: WANStatuses[]) => {
          setWANStatuses(result);
        })
        .catch((error) => {
          setWANStatuses([]);
        })
        .finally(() => {
          setLoadingWANStatuses(false);
        });
    }
  };

  const loadCardFree = function () {
    if (showCardFreeInfo) {
      const urlCF = config.rootAPIList.olo;
      setLoadingCardFreeStatuses(true);
      execute("GET", `${config.rootAPIUrl}${urlCF}`)
        .then((result: CardFreeInfo[]) => {
          setStoreCardFreeStatuses(result);
        })
        .catch((error) => {
          setStoreCardFreeStatuses([]);
        })
        .finally(() => {
          setLoadingCardFreeStatuses(false);
        });
    }
  };

  const loadPOS = function () {
    if (showPOSStatus) {
      const url = config.rootAPIList.deviceSummary;
      setLoadingPOSStatuses(true);
      execute("GET", `${config.rootAPIUrl}${url}`)
        .then((result: POSStatus[]) => {
          setStorePOSStatuses(result);
        })
        .catch((error) => {
          setStorePOSStatuses([]);
        })
        .finally(() => {
          setLoadingPOSStatuses(false);
        });
    }
  };

  const loadUberEats = function () {
    if (showUberStatus) {
      const urlCF = config.rootAPIList.uberEats;
      setLoadingUberStatuses(true);
      execute("GET", `${config.rootAPIUrl}${urlCF}`)
        .then((result) => {
          setUberStatuses(result);
        })
        .catch((error) => {
          setUberStatuses([]);
        })
        .finally(() => {
          setLoadingUberStatuses(false);
        });
    }
  };

  const loadDoorDash = function () {
    if (showDoorDash) {
      const url = config.rootAPIList.doorDash;
      setLoadingDoorDashStatuses(true);
      execute("GET", `${config.rootAPIUrl}${url}`)
        .then((result) => {
          setDoorDashStatuses(result);
        })
        .catch((error) => {
          setDoorDashStatuses([]);
        })
        .finally(() => {
          setLoadingDoorDashStatuses(false);
        });
    }
  };

  const loadDoorDashFeatureFlag = function () {
    if (showDoorDashFeatureFlag) {
      const url = config.rootAPIList.doorDashFeatureFlag;
      setLoadingDoorDashFeatureFlag(true);
      execute("GET", `${config.rootAPIUrl}${url}`)
        .then((result) => {
          setDoorDashFeatureFlag(result);
        })
        .catch((error) => {
          setDoorDashFeatureFlag({ enabled: false });
        })
        .finally(() => {
          setLoadingDoorDashFeatureFlag(false);
        });
    }
  };

  const loadWebsiteTests = function () {
    if (showWebsiteStatus) {
      setLoadingWebsiteTests(true);

      Promise.all([
        execute("GET", `${config.rootAPIUrl}${config.rootAPIList.automatedTest("Website")}`).catch(() => ({
          testName: "Website",
          passed: false,
          runDate: new Date().toISOString(),
        })),
        execute("GET", `${config.rootAPIUrl}${config.rootAPIList.automatedTest("OnlineOrdering")}`).catch(() => ({
          testName: "OnlineOrdering",
          passed: false,
          runDate: new Date().toISOString(),
        })),
      ])
        .then(([websiteResult, orderingResult]) => {
          const processedWebsiteResult: AutomatedTestResult = {
            testName: "Website",
            passed: !!websiteResult.passed,
            runDate: websiteResult.runDate,
          };

          const processedOrderingResult: AutomatedTestResult = {
            testName: "OnlineOrdering",
            passed: !!orderingResult.passed,
            runDate: orderingResult.runDate,
          };
          setAutomatedTestResult([processedWebsiteResult, processedOrderingResult]);
        })
        .finally(() => {
          setLoadingWebsiteTests(false);
        });
    }
  };

  useEffect(() => {
    loadCardFree();
  }, [showCardFreeInfo]);

  useEffect(() => {
    loadWAN(dayjs().format("YYYY-MM-DD"));
  }, [showWANInfo]);

  useEffect(() => {
    loadPOS();
  }, [showPOSStatus]);

  useEffect(() => {
    loadUberEats();
  }, [showUberStatus]);

  useEffect(() => {
    loadDoorDashFeatureFlag();
  }, [showDoorDashFeatureFlag]);

  useEffect(() => {
    loadDoorDash();
  }, [showDoorDash]);

  useEffect(() => {
    loadWebsiteTests();
  }, [showWebsiteStatus]);

  return (
    <>
      <FirstRow
        loadingWANStatuses={loadingWANStatuses}
        WANStatuses={WANStatuses}
        loadingCardFreeStatuses={loadingCardFreeStatuses}
        cardFreeStatuses={storeCardFreeStatuses}
        loadingPOSStatuses={loadingPOSStatuses}
        POSStatuses={storePOSStatuses}
        uberEatsStatuses={uberStatuses}
        loadingUberEatsStatuses={loadingUberStatuses}
        doorDashFeatureFlag={doorDashFeatureFlag}
        loadingDoorDashFeatureFlag={loadingDoorDashFeatureFlag}
        doorDashStatuses={doorDashStatuses}
        loadingDoorDashStatuses={loadingDoorDashStatuses}
      />
      <SecondRow
        loadingWANStatuses={loadingWANStatuses}
        WANStatuses={WANStatuses}
        loadingCardFreeStatuses={loadingCardFreeStatuses}
        cardFreeStatuses={storeCardFreeStatuses}
        loadingPOSStatuses={loadingPOSStatuses}
        POSStatuses={storePOSStatuses}
        loadingUberEatsStatuses={loadingUberStatuses}
        uberEatsStatuses={uberStatuses}
        doorDashFeatureFlag={doorDashFeatureFlag}
        loadingDoorDashFeatureFlag={loadingDoorDashFeatureFlag}
        loadingDoorDashStatuses={loadingDoorDashStatuses}
        doorDashStatuses={doorDashStatuses}
      ></SecondRow>
      <ThirdRow loadingWebsiteTests={loadingWebsiteTests} websiteTests={automatedTestResult}></ThirdRow>
    </>
  );
};
